import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { useFieldMappings } from '@/hooks/useFieldMappings';
import { supabase } from '@/integrations/supabase/client';

export interface FieldMapping {
  originalName: string;
  mappedName: string;
  confidence: number;
  isRequired: boolean;
}

export interface CSVDialect {
  delimiter: string;
  encoding: string;
  hasHeader: boolean;
}

interface FieldMappingPrecheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (mapping: Record<string, string>, dialect: { delimiter: string; hasHeader: boolean }) => void;
  csvContent: string;
  customerId: string;
  moduleName: string;
}

interface PrecheckResult {
  status: string;
  fields: Record<string, {
    suggested?: { header: string; score: number };
    accepted?: { header: string; score: number };
    confidence?: number;
    status?: 'confirmed' | 'uncertain';
    reason?: string;
  }>;
  deterministic: Record<string, string>;
  confidence: Record<string, number>;
  missing_required: string[];
  warnings: string[];
  sample_normalized_rows: any[];
  headers: string[];
  delimiter: string;
  ai_used: boolean;
  has_headers?: boolean;
  content_analysis?: string;
}

const TARGET_FIELDS = [
  { key: "Datum", label: "Datum", required: true },
  { key: "Händelse", label: "Händelse", required: true },
  { key: "Belopp", label: "Belopp", required: true },
  { key: "Saldo", label: "Saldo", required: false, recommended: true },
  { key: "Förfallodatum", label: "Förfallodatum", required: false },
  { key: "TransaktionsID", label: "TransaktionsID", required: false },
  { key: "DeklareratBelopp", label: "Deklarerat Belopp", required: false }
];

export function FieldMappingPrecheckModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  csvContent, 
  customerId, 
  moduleName 
}: FieldMappingPrecheckModalProps) {
  const [precheckResult, setPrecheckResult] = useState<PrecheckResult | null>(null);
  const [currentMapping, setCurrentMapping] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { saveMapping } = useFieldMappings();
  const [rawPreviewRows, setRawPreviewRows] = useState<string[][]>([]);

  useEffect(() => {
    if (isOpen && csvContent) {
      runPrecheck();
    }
  }, [isOpen, csvContent]);

  useEffect(() => {
    // Sätt accepted-värden när precheck är klar och auto-förvälj vid hög säkerhet
    if (precheckResult?.fields) {
      const initialMapping: Record<string, string> = {};
      
      console.log('[FieldMappingPrecheckModal] precheckResult.fields:', precheckResult.fields);
      console.log('[FieldMappingPrecheckModal] available headers:', precheckResult.headers);
      
      for (const [field, suggestion] of Object.entries(precheckResult.fields)) {
        const acceptedHeader = suggestion.accepted?.header;
        const suggestedHeader = suggestion.suggested?.header;
        const score = suggestion.confidence ?? suggestion.accepted?.score ?? suggestion.suggested?.score ?? 0;

        // Enhanced debugging: Log all candidates
        console.log(`[FieldMapping Debug] ${field}:`, {
          suggested: suggestion.suggested,
          accepted: suggestion.accepted,
          status: suggestion.status,
          reason: suggestion.reason,
          confidence: score,
          availableHeaders: precheckResult.headers
        });

        if (acceptedHeader) {
          initialMapping[field] = acceptedHeader;
          console.log(`[FieldMappingPrecheckModal] Auto-accepted ${field} -> ${acceptedHeader} (score: ${score})`);
        } else if (suggestedHeader && score >= 0.85 && suggestion.status !== 'uncertain') {
          // Fallback: auto-förvälj suggested vid hög säkerhet om accepted saknas
          initialMapping[field] = suggestedHeader;
          console.log(`[FieldMappingPrecheckModal] Auto-selected suggested ${field} -> ${suggestedHeader} (score: ${score})`);
        } else if (suggestedHeader) {
          console.log(`[FieldMappingPrecheckModal] NOT auto-accepting ${field} -> ${suggestedHeader} (score: ${score}, status: ${suggestion.status}, reason: ${suggestion.reason})`);
        } else {
          console.log(`[FieldMappingPrecheckModal] No suggestion for ${field}`);
        }
      }
      
      console.log('[FieldMappingPrecheckModal] Final initialMapping:', initialMapping);
      setCurrentMapping(initialMapping);
    }

    // Bygg rådata-förhandsvisning från original CSV (första raderna)
    if (precheckResult?.delimiter && csvContent) {
      const lines = csvContent.trim().split(/\r?\n/).slice(0, 6);
      const rows = lines.map(line => line.split(precheckResult.delimiter));
      setRawPreviewRows(rows);
    }
  }, [precheckResult, csvContent]);

  const runPrecheck = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate csvContent before sending
      if (!csvContent || csvContent.trim().length === 0) {
        throw new Error('CSV-innehåll saknas eller är tomt');
      }

      console.log('[FieldMappingPrecheckModal] Sending precheck request, CSV length:', csvContent.length);

      const { data, error } = await supabase.functions.invoke('testlab-mapping-precheck', {
        body: { csvContent, maxRows: 500 }
      });

      console.log('[FieldMappingPrecheckModal] Precheck response:', { data, error });

      if (error) {
        console.error('[FieldMappingPrecheckModal] Precheck error:', error);
        throw error;
      }

      setPrecheckResult(data);
      // currentMapping sätts nu via useEffect
    } catch (err: any) {
      console.error('[FieldMappingPrecheckModal] Precheck failed:', err);
      setError(`Precheck misslyckades: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (targetField: string, sourceHeader: string) => {
    setCurrentMapping(prev => ({
      ...prev,
      [targetField]: sourceHeader === "unmapped" ? "" : sourceHeader
    }));
  };

  const handleConfirm = async () => {
    if (!precheckResult) return;

    try {
      // Konvertera "Kolumn N" endast om filen verkligen har rubriker som ser rimliga ut
      const resolvedMapping: Record<string, string> = {};

      const headers = precheckResult.headers || [];
      const hasHeaders = Boolean(precheckResult.has_headers);
      const headersLookLikeData = Array.isArray(headers)
        ? headers.filter(Boolean).filter((h) => {
            const s = String(h).trim();
            const isDate = /^(\d{4}-\d{2}-\d{2}|\d{2}[\/.-]\d{2}[\/.-]\d{4}|\d{8})$/.test(s);
            const isNumericish = /^-?[\d\s\u00A0\u2009\u202F.,]+$/.test(s);
            return isDate || isNumericish;
          }).length > (headers?.length || 0) * 0.6
        : false;

      for (const [targetField, sourceHeader] of Object.entries(currentMapping)) {
        if (!sourceHeader) continue;

        const kolumnMatch = /^Kolumn\s+(\d+)$/i.exec(sourceHeader);
        if (kolumnMatch) {
          const idx = Math.max(0, Number(kolumnMatch[1]) - 1);
          // Endast resolva till verkliga headers om:
          // 1) precheck angett att filen har headers, OCH
          // 2) headers inte ser ut som datainnehåll (t.ex. siffror/datum)
          if (hasHeaders && !headersLookLikeData && idx < headers.length) {
            resolvedMapping[targetField] = headers[idx];
            console.log(`🔄 Resolved "${sourceHeader}" → "${headers[idx]}" for ${targetField}`);
          } else {
            // Behåll Kolumn N för index-baserad mappning i workern
            resolvedMapping[targetField] = sourceHeader;
            console.log(`➡️ Keeping index-based mapping: ${targetField} -> ${sourceHeader} (hasHeaders=${hasHeaders}, headersLookLikeData=${headersLookLikeData})`);
          }
        } else {
          resolvedMapping[targetField] = sourceHeader;
        }
      }

      // Save the resolved mapping
      await saveMapping(
        customerId,
        moduleName,
        resolvedMapping,
        {
          delimiter: precheckResult.delimiter,
          encoding: 'utf-8',
          hasHeader: Boolean(precheckResult.has_headers)
        }
      );

      onConfirm(resolvedMapping, { delimiter: precheckResult.delimiter, hasHeader: Boolean(precheckResult.has_headers) });
    } catch (err: any) {
      setError(`Kunde inte spara mappning: ${err.message}`);
    }
  };

  const getConfidenceBadge = (field: string) => {
    if (!precheckResult?.fields[field]) return null;
    const suggestion = precheckResult.fields[field];
    const score = suggestion.confidence || suggestion.accepted?.score || suggestion.suggested?.score;
    if (!score) return null;
    
    const percentage = Math.round(score * 100);
    const isUncertain = suggestion.status === 'uncertain';
    
    if (score >= 0.85 && !isUncertain) {
      return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
        {percentage}% - Säker
      </Badge>;
    }
    if (score >= 0.7) {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
        {percentage}% - Måttlig
      </Badge>;
    }
    return <Badge variant="secondary" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100">
      {percentage}% - Osäker
    </Badge>;
  };

  const applySuggestion = (field: string) => {
    if (!precheckResult?.fields[field]?.suggested) return;
    const suggested = precheckResult.fields[field].suggested!;
    setCurrentMapping(prev => ({
      ...prev,
      [field]: suggested.header
    }));
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fältmappning för CSV-import</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2">Analyserar CSV-filen...</span>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {precheckResult && (
          <div className="space-y-6">
            {/* Status Overview */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span>CSV analyserad</span>
              </div>
              <div className="text-sm text-muted-foreground">
                {precheckResult.headers.length} kolumner, delimiter: '{precheckResult.delimiter}'
                {precheckResult.ai_used && (
                  <Badge variant="outline" className="ml-2">AI-förbättrad</Badge>
                )}
                {!precheckResult.has_headers && (
                  <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200">
                    Innehållsbaserad analys
                  </Badge>
                )}
              </div>
            </div>

            {/* Info om innehållsbaserad analys */}
            {!precheckResult.has_headers && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Ingen kolumnrubrik hittades.</strong> Systemet har analyserat innehållet i varje kolumn för att identifiera:
                  <ul className="list-disc ml-4 mt-2">
                    <li><strong>Datum</strong>: Kolumn med datumformat som ökar över tid</li>
                    <li><strong>Händelse</strong>: Textkolumn med skattespecifika ord (moms, preliminärskatt, etc.)</li>
                    <li><strong>Belopp</strong>: Numerisk kolumn med både positiva och negativa värden</li>
                    <li><strong>Saldo</strong>: Numerisk kolumn där värden följer ackumulerat mönster</li>
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Info om förfallodatum */}
            {!precheckResult.deterministic.Förfallodatum && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Förfallodatum saknas i filen.</strong> Systemet kommer automatiskt att matcha transaktioner mot <strong>skattekalendern 2025</strong> för att beräkna korrekta förfallodatum baserat på:
                  <ul className="list-disc ml-4 mt-2">
                    <li>Transaktionstyp (preliminärskatt, moms, arbetsgivaravgifter)</li>
                    <li>Period (månad/kvartal)</li>
                    <li>Företagsstorlek ({'<'}40M kr eller {'>'}40M kr omsättning)</li>
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Warnings and Missing Fields */}
            {(precheckResult.warnings.length > 0 || precheckResult.missing_required.length > 0) && (
              <div className="space-y-2">
                {precheckResult.missing_required.length > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Obligatoriska fält saknas: {precheckResult.missing_required.join(', ')}
                    </AlertDescription>
                  </Alert>
                )}
                
                {precheckResult.warnings.map((warning, index) => (
                  <Alert key={index}>
                    <Info className="h-4 w-4" />
                    <AlertDescription>{warning}</AlertDescription>
                  </Alert>
                ))}
              </div>
            )}

            {/* Field Mapping Interface */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Fältmappning</h3>
              
              {/* Obligatoriska fält */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold mb-3">Obligatoriska fält</h4>
                <div className="grid gap-4">
                  {TARGET_FIELDS.filter(f => f.required).map(field => {
                    const suggestion = precheckResult.fields?.[field.key];
                    const hasAccepted = !!suggestion?.accepted;
                    const hasSuggested = !!suggestion?.suggested;
                    const isUncertain = suggestion?.status === 'uncertain';
                    const score = suggestion?.confidence || suggestion?.accepted?.score || suggestion?.suggested?.score;
                    
                    return (
                      <div key={field.key} className={`flex items-start gap-4 p-3 border rounded-lg ${
                        isUncertain && hasSuggested ? 'border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-950' : ''
                      }`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{field.label}</span>
                            <Badge variant="destructive" className="text-xs">Obligatorisk</Badge>
                          </div>
                          
                          {hasSuggested && !hasAccepted && (
                            <div className="mt-2 flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={`${
                                  isUncertain 
                                    ? 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-100 dark:border-yellow-700' 
                                    : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-700'
                                }`}>
                                  Föreslagen: {suggestion.suggested!.header} ({Math.round(suggestion.suggested!.score * 100)}%)
                                </Badge>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => applySuggestion(field.key)}
                                  className="h-6 text-xs"
                                >
                                  Applicera
                                </Button>
                              </div>
                              {isUncertain && (
                                <Alert className="py-2">
                                  <AlertTriangle className="h-3 w-3" />
                                  <AlertDescription className="text-xs">
                                    <strong>Osäker mappning - vänligen bekräfta eller välj manuellt</strong>
                                  </AlertDescription>
                                </Alert>
                              )}
                            </div>
                          )}
                          
                          {suggestion?.reason && (
                            <div className="mt-1 text-xs text-muted-foreground">
                              {suggestion.reason}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <Select
                            value={currentMapping[field.key] || "unmapped"}
                            onValueChange={(value) => handleMappingChange(field.key, value)}
                          >
                            <SelectTrigger className={
                              isUncertain && hasSuggested 
                                ? 'border-yellow-400 dark:border-yellow-600' 
                                : ''
                            }>
                              <SelectValue placeholder="Välj kolumn..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unmapped">Ej mappad</SelectItem>
                              {precheckResult.headers.map(header => (
                                <SelectItem key={header} value={header}>
                                  {header}
                                  {hasSuggested && suggestion?.suggested?.header === header && (
                                    <span className="ml-2 text-xs text-muted-foreground">
                                      ({Math.round(suggestion.suggested!.score * 100)}%)
                                    </span>
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Rekommenderade fält */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold mb-3">Starkt rekommenderade fält</h4>
                <div className="grid gap-4">
                  {TARGET_FIELDS.filter(f => !f.required && (f as any).recommended).map(field => {
                    const suggestion = precheckResult.fields?.[field.key];
                    const hasAccepted = !!suggestion?.accepted;
                    const hasSuggested = !!suggestion?.suggested;
                    const isUncertain = suggestion?.status === 'uncertain';
                    const isMapped = currentMapping[field.key] && currentMapping[field.key] !== 'unmapped';
                    
                    return (
                      <div key={field.key} className="flex items-start gap-4 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{field.label}</span>
                            {isMapped && <CheckCircle className="h-4 w-4 text-green-500" />}
                            {!isMapped && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                          </div>
                          {field.key === 'Saldo' && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Saldo möjliggör saldokontroll och avstämning. Utan saldo begränsas analysen.
                            </p>
                          )}
                          
                          {hasSuggested && !hasAccepted && (
                            <div className="mt-2">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-700">
                                Föreslagen: {suggestion.suggested!.header}
                              </Badge>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <Select
                            value={currentMapping[field.key] || 'unmapped'}
                            onValueChange={(value) => handleMappingChange(field.key, value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Välj kolumn..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unmapped">Ej mappad</SelectItem>
                              {precheckResult.headers.map(header => (
                                <SelectItem key={header} value={header}>
                                  {header}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Valfria fält */}
              <div>
                <h4 className="text-sm font-semibold mb-3">Övriga valfria fält</h4>
                <div className="grid gap-4">
                  {TARGET_FIELDS.filter(f => !f.required && !(f as any).recommended).map(field => {
                    const suggestion = precheckResult.fields?.[field.key];
                    const hasSuggested = !!suggestion?.suggested;
                    
                    return (
                      <div key={field.key} className="flex items-start gap-4 p-3 border rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{field.label}</span>
                          </div>
                          
                          {hasSuggested && (
                            <div className="mt-2">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-700">
                                Föreslagen: {suggestion.suggested!.header}
                              </Badge>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <Select
                            value={currentMapping[field.key] || 'unmapped'}
                            onValueChange={(value) => handleMappingChange(field.key, value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Välj kolumn..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unmapped">Ej mappad</SelectItem>
                              {precheckResult.headers.map(header => (
                                <SelectItem key={header} value={header}>
                                  {header}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>


            {/* Rådata (förhandsvisning – första raderna) */}
            {rawPreviewRows.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Förhandsvisning (rådata)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-border">
                    <thead>
                      <tr className="bg-muted">
                        {Array.from({ length: Math.max(...rawPreviewRows.map(r => r.length), 0) }).map((_, i) => (
                          <th key={i} className="border border-border p-2 text-left">
                            {`Kolumn ${i + 1}`}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rawPreviewRows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-muted/50">
                          {Array.from({ length: Math.max(...rawPreviewRows.map(r => r.length), 0) }).map((_, i) => (
                            <td key={i} className="border border-border p-2">
                              {row[i] ?? ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Sample Data Preview */}
            {precheckResult.sample_normalized_rows.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Förhandsvisning (normaliserad data)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-border">
                    <thead>
                      <tr className="bg-muted">
                        {TARGET_FIELDS.filter(f => currentMapping[f.key]).map(field => (
                          <th key={field.key} className="border border-border p-2 text-left">
                            {field.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {precheckResult.sample_normalized_rows.slice(0, 5).map((row, index) => (
                        <tr key={index} className="hover:bg-muted/50">
                          {TARGET_FIELDS.filter(f => currentMapping[f.key]).map(field => (
                            <td key={field.key} className="border border-border p-2">
                              {row[field.key] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Avbryt
              </Button>
              <Button 
                onClick={handleConfirm}
                disabled={precheckResult.missing_required.length > 0}
              >
                Bekräfta mappning
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}